// src/pages/UsersDirectory.js

import React, { useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";

import {
  getUsersByDivision,
  getUserById,
  updateUser,
  deleteUser,
  sendPasswordOffer,
  sendRegistrationInvitation,
} from "../api/user";
import { notifyError } from "../utils/toasts";

function UsersDirectory({ divisionId, enterpriseId }) {
  const [users, setUsers] = useState([]);            // Список пользователей
  const [searchQuery, setSearchQuery] = useState(""); 
  const [filteredData, setFilteredData] = useState([]);

  // Пагинация
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;

  // Режим: добавление / редактирование
  const [isEditing, setIsEditing] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);

  // Поля для формы редактирования
  const [editName, setEditName] = useState("");
  const [editSurname, setEditSurname] = useState("");
  const [editEmail, setEditEmail] = useState("");

  // Режим: добавление (отправка приглашения)
  const [isAdding, setIsAdding] = useState(false);
  // Поля для добавления
  const [addEmail, setAddEmail] = useState("");

  // Модальное окно удаления
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Модальное окно сброса пароля
  const [showResetModal, setShowResetModal] = useState(false);
  const [userToReset, setUserToReset] = useState(null);

  // Получаем список пользователей
  useEffect(() => {
    const fetchUsers = async () => {
      if (divisionId) {
        try {
          const data = await getUsersByDivision(divisionId);
          setUsers(data);
        } catch (error) {
          notifyError(error?.response?.data?.message || error.message);
        }
      }
    };
    fetchUsers();
  }, [divisionId]);

  // Фильтрация
  useEffect(() => {
    const lowerQuery = searchQuery.toLowerCase();
    const filtered = users.filter((u) =>
      Object.values(u).some((val) => 
        val && val.toString().toLowerCase().includes(lowerQuery)
      )
    );
    setFilteredData(filtered);
    setCurrentPage(0);
  }, [searchQuery, users]);

  // Пагинация
  const offset = currentPage * itemsPerPage;
  const currentItems = filteredData.slice(offset, offset + itemsPerPage);
  const pageCount = Math.ceil(filteredData.length / itemsPerPage);

  const handlePageClick = (selected) => {
    setCurrentPage(selected.selected);
  };


  const handleAddClick = () => {
    setIsEditing(false);
    setIsAdding(true);
    setAddEmail("");
  };
  const handleSendInvitation = async () => {
    if (!addEmail.trim()) return;
    // POST /user/send-registration-invitation
    // { enterpriseId, divisionId, email }
    try {
      await sendRegistrationInvitation({ 
        enterpriseId,
        divisionId,
        email: addEmail.trim(),
      });

      // Закрыть форму
      setIsAdding(false);
      setAddEmail("");
    } catch (error) {
      notifyError(error?.response?.data?.message || error.message);
    }
  };
  const handleCancelAdd = () => {
    setIsAdding(false);
    setAddEmail("");
  };

  // ================= EDIT =================
  const handleRowClick = async (user) => {
    // Открываем форму редактирования
    setIsAdding(false);
    setIsEditing(true);
    setEditingUserId(user.id);

    try {
      const fullUser = await getUserById(user.id);
      setEditName(fullUser.name);
      setEditSurname(fullUser.surname);
      setEditEmail(fullUser.email);
    } catch (error) {
      notifyError(error?.response?.data?.message || error.message);
    }
  };

  const handleSaveEdit = async () => {
    if (!editName.trim() || !editSurname.trim() || !editEmail.trim()) return;
    // PUT /user/{id}
    try {
      await updateUser(editingUserId, {
        name: editName.trim(),
        surname: editSurname.trim(),
        email: editEmail.trim(),
      });
      // Обновить список
      const data = await getUsersByDivision(divisionId);
      setUsers(data);
      // Сбросить форму
      setIsEditing(false);
      setEditingUserId(null);
      setEditName("");
      setEditSurname("");
      setEditEmail("");
    } catch (error) {
      notifyError(error?.response?.data?.message || error.message);
    }
  };
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingUserId(null);
    setEditName("");
    setEditSurname("");
    setEditEmail("");
  };

  // ================= DELETE =================
  const handleDeleteClick = (user, e) => {
    e.stopPropagation();
    setUserToDelete(user);
    setShowDeleteModal(true);
  };
  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      await deleteUser(userToDelete.id);
      const data = await getUsersByDivision(divisionId);
      setUsers(data);
    } catch (error) {
      notifyError(error?.response?.data?.message || error.message);
    } finally {
      setShowDeleteModal(false);
      setUserToDelete(null);
    }
  };

  // ================= RESET PASSWORD =================
  const handleResetClick = (user, e) => {
    e.stopPropagation();
    setUserToReset(user);
    setShowResetModal(true);
  };
  const confirmResetPassword = async () => {
    if (!userToReset) return;
    try {
      await sendPasswordOffer(userToReset.id);
      // Возможно, показ какого-то уведомления "Инструкции отправлены"
    } catch (error) {
      notifyError(error?.response?.data?.message || error.message);
    } finally {
      setShowResetModal(false);
      setUserToReset(null);
    }
  };

  // Определяем столбцы
  // #, Имя, Фамилия, E-Mail, Сброс пароля, Удалить
  // Хотя Сброс/Удалить вынесем в один "Actions" столбец
  const columns = [
    { header: "#", accessor: "index" },
    { header: "Имя", accessor: "name" },
    { header: "Фамилия", accessor: "surname" },
    { header: "E-Mail", accessor: "email" },
  ];

  return (
    <div className="container">
      <h1>Справочник пользователей</h1>
      <div className="card">
        {/* Шапка карточки */}
        <div className="card-header">
          {/* Поле поиска */}
          <input
            type="text"
            placeholder="Поиск..."
            className="form-control"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "200px",
              display: "inline-block",
              marginRight: "20px",
            }}
          />

          {/* Кнопка "Добавить" */}
          {!isEditing && !isAdding && (
            <button
              className="btn btn-primary"
              onClick={handleAddClick}
            >
              Добавить
            </button>
          )}
        </div>

        {/* Таблица */}
        <div className="card-body p-0">
          <table className="table table-bordered table-striped table-hover projects">
            <thead>
              <tr>
                {columns.map((col, idx) => (
                  <th key={idx}>{col.header}</th>
                ))}
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((user, idx) => {
                const rowIndex = offset + idx + 1;
                return (
                  <tr
                    key={user.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => handleRowClick(user)}
                  >
                    <td>{rowIndex}</td>
                    <td>{user.name}</td>
                    <td>{user.surname}</td>
                    <td>{user.email}</td>
                    <td>
                      {/* Сброс пароля */}
                      <button
                        className="btn btn-warning btn-sm"
                        onClick={(e) => handleResetClick(user, e)}
                      >
                        Сбросить пароль
                      </button>
                      {/* Удалить */}
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={(e) => handleDeleteClick(user, e)}
                        style={{ marginLeft: "5px" }}
                      >
                        Удалить
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Пагинация */}
        <div className="card-footer">
          <div className="d-flex justify-content-end">
            <ReactPaginate
              previousLabel="Пред."
              nextLabel="След."
              breakLabel="..."
              breakClassName="page-item"
              breakLinkClassName="page-link"
              pageCount={pageCount}
              marginPagesDisplayed={2}
              pageRangeDisplayed={3}
              onPageChange={handlePageClick}
              containerClassName="pagination"
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link"
              nextClassName="page-item"
              nextLinkClassName="page-link"
              activeClassName="active"
              forcePage={currentPage}
            />
          </div>
        </div>
      </div>

      {/* Карточка нижняя (редактор / добавление) */}
      <div className="card mt-4">
        <div className="card-body">
          {isEditing && !isAdding && (
            <>
              <h5>Редактировать пользователя</h5>
              <div className="form-group">
                <label>Имя</label>
                <input
                  type="text"
                  className="form-control"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Фамилия</label>
                <input
                  type="text"
                  className="form-control"
                  value={editSurname}
                  onChange={(e) => setEditSurname(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>E-Mail</label>
                <input
                  type="email"
                  className="form-control"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                />
              </div>
              <div className="mt-2">
                <button
                  className="btn btn-success me-2"
                  onClick={handleSaveEdit}
                  disabled={
                    !editName.trim() ||
                    !editSurname.trim() ||
                    !editEmail.trim()
                  }
                >
                  Сохранить
                </button>
                <button className="btn btn-secondary" onClick={handleCancelEdit}>
                  Отмена
                </button>
              </div>
            </>
          )}

          {isAdding && !isEditing && (
            <>
              <h5>Отправить приглашение новому пользователю</h5>
              <div className="form-group">
                <label>E-Mail</label>
                <input
                  type="email"
                  className="form-control"
                  value={addEmail}
                  onChange={(e) => setAddEmail(e.target.value)}
                />
              </div>
              <div className="mt-2">
                <button
                  className="btn btn-success me-2"
                  onClick={handleSendInvitation}
                  disabled={!addEmail.includes("@")}
                >
                  Отправить приглашение
                </button>
                <button className="btn btn-secondary" onClick={handleCancelAdd}>
                  Отмена
                </button>
              </div>
            </>
          )}

          {!isEditing && !isAdding && (
            <p className="text-muted">
              Выберите пользователя из списка для редактирования или нажмите «Добавить»
            </p>
          )}
        </div>
      </div>

      {/* Модальное окно подтверждения удаления */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Подтверждение удаления</Modal.Title>
        </Modal.Header>
        <Modal.Body>Вы уверены, что хотите удалить пользователя?</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            Отмена
          </Button>
          <Button variant="danger" onClick={confirmDeleteUser}>
            Удалить
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Модальное окно сброса пароля */}
      <Modal show={showResetModal} onHide={() => setShowResetModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Сбросить пароль</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Отправить письмо для сброса пароля этому пользователю?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResetModal(false)}>
            Отмена
          </Button>
          <Button variant="warning" onClick={confirmResetPassword}>
            Сбросить
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default UsersDirectory;
